import { ZButton } from "@/shared/components/button";
import { TruncatedText } from "@/shared/components/TruncatedText";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";
import CheckboxIcon from "@/shared/icons/checkbox-icon";
import CircleChevronDownIcon from "@/shared/icons/circle-chevron-down-icon";
import FileUpIcon from "@/shared/icons/file-up-icon";
import RadioIcon from "@/shared/icons/radio-icon";
import TextInitialIcon from "@/shared/icons/text-initial-icon";
import { addNotification } from "@/shared/states/notification";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Select,
  SelectItem,
  Slider,
  Switch,
} from "@nextui-org/react";
import {
  ChevronDown,
  CircleHelp,
  Copy,
  CornerDownRight,
  Ellipsis,
  GripVertical,
  Trash2,
  X as XIcon,
} from "lucide-react";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import DeleteQuestionModal from "./DeleteQuestionModal";
import {
  getAvailableSubQuestions,
  getQuestionDisplayText,
  moveUnmappedQuestionAfterSiblings,
  type Question,
} from "./questionTreeUtils";

export const QUESTION_TYPES = [
  { label: "Text Field", value: "free_text", icon: TextInitialIcon, showDividerAfter: true },
  { label: "Single Choice", value: "single_choice", icon: RadioIcon },
  { label: "Multiple Choice", value: "multiple_choice", icon: CheckboxIcon },
  { label: "Dropdown", value: "dropdown", icon: CircleChevronDownIcon },
  { label: "File Upload", value: "file_upload", icon: FileUpIcon, showDividerBefore: true },
];

const optionsLabel = {
  free_text: "Text Field",
  single_choice: "Single-Choice Options",
  multiple_choice: "Multiple-Choice Options",
  dropdown: "Dropdown Options",
  file_upload: "File Upload",
};

const QuestionItem = ({
  id,
  index,
  onRemove,
  onDuplicate,
  isInvalid = false,
  duplicateOptionIndices,
  onEdit,
  questionRef,
  allQuestions = [],
  onSubQuestionMap,
  getDisplayNumber,
  hideComments = false,
  hideSubQuestionMapping = false,
  showFileUploadType = false,
}: {
  id: string;
  index: number;
  onRemove: () => void;
  onDuplicate?: (questionData: any) => void;
  isInvalid?: boolean;
  duplicateOptionIndices?: Set<number>;
  onEdit?: (questionId: string) => void;
  questionRef?: MutableRefObject<Map<string, HTMLDivElement>>;
  allQuestions?: any[];
  onSubQuestionMap?: (parentQuestionId: string, optionIndex: number, subQuestionId: string | null) => void;
  getDisplayNumber?: (questionId: string) => string;
  hideComments?: boolean;
  hideSubQuestionMapping?: boolean;
  showFileUploadType?: boolean;
}) => {
  const {
    control,
    register,
    setValue,
    watch,
    getValues,
    formState: { errors },
    trigger,
  } = useFormContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const [activeSubQuestionOption, setActiveSubQuestionOption] = useState<number | null>(null);

  // Calculate display number for this question
  const getQuestionDisplayNumber = () => {
    const currentQuestion = watch(`questions.${index}`);

    if (getDisplayNumber && currentQuestion?.id) {
      return getDisplayNumber(currentQuestion.id);
    }

    // Fallback to index + 1 if getDisplayNumber is not provided
    return String(index + 1);
  };

  // Get validation errors for this question
  const questionError =
    errors?.questions && Array.isArray(errors.questions) ? (errors.questions[index] as any) : undefined;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const questionId = watch(`questions.${index}.id`);

  // Register this question's ref when component mounts or ID changes
  useEffect(() => {
    if (questionRef && itemRef.current && questionId) {
      questionRef.current.set(questionId, itemRef.current);
      return () => {
        questionRef.current.delete(questionId);
      };
    }
  }, [questionRef, questionId]);

  // Handle focus/input events to clear invalid state
  const handleInputFocus = useCallback(() => {
    if (isInvalid && onEdit && questionId) {
      onEdit(questionId);
    }
  }, [isInvalid, onEdit, questionId]);

  // Watch the question value for display purposes
  const responseChoices = watch(`questions.${index}.response_choices`);

  // Clear isInvalid state when validation errors are cleared
  useEffect(() => {
    if (isInvalid && !questionError && onEdit && questionId) {
      console.log("Clearing invalid state for question:", questionId);
      onEdit(questionId);
    }
  }, [questionError, isInvalid, onEdit, questionId]);

  const questionType = watch(`questions.${index}.question_type`) as unknown as
    | "free_text"
    | "single_choice"
    | "multiple_choice"
    | "file_upload"
    | undefined;
  const isDropdown = watch(`questions.${index}.is_dropdown`);

  // Derive the actual type considering the is_dropdown flag
  const type = isDropdown && questionType === "single_choice" ? "dropdown" : questionType;

  // Delete question handler (only removes from local state, no API call)
  const handleDeleteQuestion = () => {
    setIsDeleteModalOpen(false);
    // Call the onRemove callback to remove from form state
    onRemove();
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDuplicateQuestion = () => {
    if (!onDuplicate) {
      addNotification({
        type: "error",
        message: "Duplicate functionality is not available.",
      });
      return;
    }

    const questionData = getValues(`questions.${index}`);
    onDuplicate(questionData);
  };

  const getQuestionTypeIcon = (questionType: string) => {
    switch (questionType) {
      case "free_text":
        return <TextInitialIcon className="w-4 h-4" />;
      case "single_choice":
        return <RadioIcon className="w-4 h-4" />;
      case "multiple_choice":
        return <CheckboxIcon className="w-4 h-4" />;
      case "dropdown":
        return <CircleChevronDownIcon className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const handleQuestionTypeChange = (newType: string) => {
    setValue(`questions.${index}.question_type`, newType === "dropdown" ? "single_choice" : newType, {
      shouldDirty: true,
    });

    // If changing to single_choice, multiple_choice, or dropdown and no options exist, add empty option
    const currentChoices = getValues(`questions.${index}.response_choices`) || [];
    if (["single_choice", "multiple_choice", "dropdown"].includes(newType) && currentChoices.length === 0) {
      setValue(`questions.${index}.response_choices`, [""], { shouldDirty: true });
    }
    if (newType === "free_text") {
      // If changing to free_text, remove all options
      setValue(`questions.${index}.response_choices`, [], { shouldDirty: true });
    }

    // Update is_dropdown flag
    setValue(`questions.${index}.is_dropdown`, newType === "dropdown", { shouldDirty: true });
  };

  // For response_choices array
  const {
    fields: optionFields,
    append: appendOption,
    remove: removeOption,
  } = useFieldArray({
    control,
    name: `questions.${index}.response_choices`,
  });

  // Ensure choice-based questions always have at least one option field
  useEffect(() => {
    if (type && ["single_choice", "multiple_choice", "dropdown"].includes(type) && optionFields.length === 0) {
      appendOption("");
    }
  }, [type, optionFields.length, appendOption]);

  const handleAddOption = () => {
    appendOption("");

    // Recalculate duplicate errors after adding an option
    // Use setTimeout to ensure state is updated after option addition
    setTimeout(() => {
      if (onEdit) {
        onEdit(id);
      }
    }, 0);
  };

  const handleRemoveOption = (optionIndex: number) => {
    // If this is the only option, just clear its text and remove sub-question mapping
    if (optionFields.length === 1) {
      // Clear the option text
      setValue(`questions.${index}.response_choices.${optionIndex}`, "", { shouldDirty: true });

      // Remove any sub-question mapped to this option
      handleRemoveSubQuestion(optionIndex);

      // Recalculate duplicate errors after removal
      if (onEdit) {
        onEdit(id);
      }

      return;
    }

    // First, handle child question mappings before removing the option
    const currentQuestionId = watch(`questions.${index}.id`);

    // Collect IDs of questions that will be unmapped
    const unmappedQuestionIds: string[] = [];

    // Find all child questions mapped to this parent question
    let updatedQuestions = allQuestions.map((q: any) => {
      // Skip questions not mapped to this parent
      if (q.parent_question_id !== currentQuestionId) {
        return q;
      }

      const childOptionIndex = q.parent_question_response_option_index;

      // If child's option index is null/undefined (text field mapping), keep as is
      if (childOptionIndex === null || childOptionIndex === undefined) {
        return q;
      }

      // If child was mapped to the deleted option, unmap it (convert to root question)
      if (childOptionIndex === optionIndex) {
        unmappedQuestionIds.push(q.id);
        return {
          ...q,
          parent_question_id: undefined,
          parent_question_response_option_index: undefined,
        };
      }

      // If child was mapped to an option after the deleted one, shift its index down by 1
      if (childOptionIndex > optionIndex) {
        return {
          ...q,
          parent_question_response_option_index: childOptionIndex - 1,
        };
      }

      // Child was mapped to an option before the deleted one, no change needed
      return q;
    });

    // Move unmapped questions to after all their siblings
    for (const unmappedId of unmappedQuestionIds) {
      updatedQuestions = moveUnmappedQuestionAfterSiblings(
        updatedQuestions as Question[],
        unmappedId,
        currentQuestionId,
      );
    }

    // Update all questions in the form
    setValue("questions", updatedQuestions, { shouldDirty: true });

    // Now remove the option from the parent question
    removeOption(optionIndex);

    // Recalculate duplicate errors after removal
    // Use setTimeout to ensure state is updated after option removal
    setTimeout(() => {
      if (onEdit) {
        onEdit(id);
      }
    }, 0);
  };

  const handleSubQuestionClick = (optionIndex: number) => {
    setActiveSubQuestionOption(activeSubQuestionOption === optionIndex ? null : optionIndex);
  };

  const handleSubQuestionSelect = (optionIndex: number, subQuestionId: string) => {
    console.log("OptionIndex : ", optionIndex, " SubQuestionId : ", subQuestionId);
    if (onSubQuestionMap && questionId) {
      // Check if there's already a mapped question for this option
      const currentlyMappedQuestion = getMappedSubQuestionForOption(optionIndex);

      // If there's already a mapped question and we're selecting a different one, unmap the old one first
      if (currentlyMappedQuestion && currentlyMappedQuestion.id !== subQuestionId) {
        onSubQuestionMap(questionId, optionIndex, null);
      }

      // Now map the new question
      onSubQuestionMap(questionId, optionIndex, subQuestionId);
      setActiveSubQuestionOption(null);
    }
  };

  const handleRemoveSubQuestion = (optionIndex: number) => {
    if (onSubQuestionMap && questionId) {
      onSubQuestionMap(questionId, optionIndex, null);
    }
  };

  // Get available questions for sub-question mapping using utility function
  const getAvailableQuestions = () => {
    if (!questionId) return [];
    return getAvailableSubQuestions(allQuestions as Question[], questionId);
  };

  // Format question display text - shows question number and text
  const formatQuestionDisplay = (question: any, isMapped: boolean) => {
    return getQuestionDisplayText(allQuestions as Question[], question, isMapped);
  };

  // Get mapped sub-question for an option
  const getMappedSubQuestionForOption = (optionIndex: number) => {
    const currentQuestion = watch(`questions.${index}`);

    // For text fields (optionIndex === -1), find question with null parent_question_response_option_index
    if (optionIndex === -1) {
      return allQuestions.find(
        (q) =>
          q.parent_question_id === questionId &&
          (q.parent_question_response_option_index === null || q.parent_question_response_option_index === undefined),
      );
    }

    // For choice-based questions, match by option INDEX (not text) to handle duplicate option text
    return allQuestions.find(
      (q) => q.parent_question_id === questionId && q.parent_question_response_option_index === optionIndex,
    );
  };

  const hasAnySubQuestionsMapped = () => {
    if (!questionId) return false;

    return allQuestions.some((q: any) => q.parent_question_id === questionId);
  };

  const renderOptions = () => {
    // Handle file_upload type
    if (type === "file_upload") {
      const maxFiles = watch(`questions.${index}.max_files`) || 5;

      return (
        <tr>
          <td className="w-full">
            <div className="mb-2">
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">Number of Files</span>
                    <ZTooltip
                      content={
                        <div className="max-w-[215px]">
                          A total of 50 files can be uploaded to this intake form. This is a shared limit across all
                          file upload fields.
                        </div>
                      }
                      placement="right"
                      showArrow
                      isDisabled={false}
                    >
                      <CircleHelp className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors" />
                    </ZTooltip>
                  </div>
                  <span className="text-xs font-medium text-gray-700">{maxFiles}/50</span>
                </div>
                <Slider
                  value={maxFiles}
                  onChange={(value) => {
                    const numValue = Array.isArray(value) ? value[0] : value;
                    setValue(`questions.${index}.max_files`, numValue, { shouldDirty: true });

                    // Calculate new total files after this change
                    const questions = getValues("questions") || [];
                    const newTotal = questions
                      .filter((q: any) => q.question_type === "file_upload")
                      .reduce((sum: number, q: any, idx: number) => {
                        // Use the new value for current question, existing value for others
                        const fileCount = idx === index ? numValue : q.max_files || 0;
                        return sum + fileCount;
                      }, 0);

                    // Update the totalFilesRequested form field
                    setValue("totalFilesRequested", newTotal, { shouldDirty: true });
                  }}
                  minValue={1}
                  maxValue={50}
                  step={1}
                  size="sm"
                  classNames={{
                    track: "bg-[#F4F4F5] h-3 rounded-lg",
                    filler: "bg-[#006FEE] h-3",
                    thumb: "w-5 h-5 bg-white border-[1.5px] border-[#006FEE] shadow-md",
                  }}
                  style={{
                    width: "100%",
                    height: "12px",
                  }}
                />
              </div>
            </div>
          </td>
        </tr>
      );
    }

    if (type === "free_text") {
      return (
        <tr>
          <td className={hideSubQuestionMapping ? "w-full" : "w-1/2"}>
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-500 grow cursor-default">Answer text preview</div>
              {!hideSubQuestionMapping && (
                <ZTooltip
                  content="Add sub-question based on this response"
                  placement="bottom"
                  showArrow
                  isDisabled={false}
                >
                  <button
                    type="button"
                    className="text-xs text-gray-500 hover:text-blue-700 flex-shrink-0"
                    onClick={() => handleSubQuestionClick(-1)}
                  >
                    <CornerDownRight className="w-4 h-4" strokeWidth={2} />
                  </button>
                </ZTooltip>
              )}
            </div>
          </td>
          {!hideSubQuestionMapping && (
            <td className="w-1/2 pl-4">
              {activeSubQuestionOption === -1 ? (
                <div className="flex items-center gap-2">
                  <Select
                    placeholder="Select a question"
                    size="sm"
                    classNames={{
                      base: "w-full",
                      trigger: "min-h-8 h-8",
                    }}
                    selectedKeys={getMappedSubQuestionForOption(-1) ? [getMappedSubQuestionForOption(-1).id] : []}
                    isOpen={true}
                    onOpenChange={(isOpen) => {
                      if (!isOpen) {
                        setActiveSubQuestionOption(null);
                      }
                    }}
                    onChange={(e) => {
                      if (e.target.value) {
                        handleSubQuestionSelect(-1, e.target.value);
                      }
                    }}
                  >
                    {getAvailableQuestions().map((q) => (
                      <SelectItem key={q.id} value={q.id} classNames={{ title: "truncate" }}>
                        {formatQuestionDisplay(q, false)}
                      </SelectItem>
                    ))}
                  </Select>
                  {getMappedSubQuestionForOption(-1) && (
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:text-red-600 flex-shrink-0"
                      onClick={() => handleRemoveSubQuestion(-1)}
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ) : (
                getMappedSubQuestionForOption(-1) && (
                  <div
                    className="flex items-center gap-2 text-sm text-gray-700 min-w-0 cursor-pointer"
                    onClick={() => handleSubQuestionClick(-1)}
                  >
                    <TruncatedText
                      text={formatQuestionDisplay(getMappedSubQuestionForOption(-1), true)}
                      className="flex-1"
                    />
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:text-red-600 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSubQuestion(-1);
                      }}
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                )
              )}
            </td>
          )}
        </tr>
      );
    }

    return (
      <>
        {optionFields.map((field, optionIndex) => {
          const mappedSubQuestion = getMappedSubQuestionForOption(optionIndex);
          const currentOptionValue = (responseChoices && responseChoices[optionIndex]) || "";
          const isOnlyOption = optionFields.length === 1;
          const isEmptyOption = !currentOptionValue.trim();
          const shouldHideRemoveButton = isOnlyOption && isEmptyOption;
          const isDropdown = getValues(`questions.${index}.is_dropdown`);

          return (
            <tr key={field.id}>
              <td className={hideSubQuestionMapping ? "w-full" : "w-1/2"}>
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-4 flex-shrink-0">
                    {type === "single_choice" && !isDropdown && (
                      <span className="h-3 w-3 rounded-full border border-gray-400" />
                    )}
                    {type === "multiple_choice" && <span className="h-3 w-3 border border-gray-400" />}
                    {(type === "dropdown" || isDropdown) && <span>{optionIndex + 1}.</span>}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <input
                      {...register(`questions.${index}.response_choices.${optionIndex}`, {
                        onChange: () => {
                          // Check if there's an error and trigger validation
                          if (questionError?.response_choices) {
                            const currentChoices = getValues(`questions.${index}.response_choices`);
                            console.log("Current choices after change:", currentChoices);
                            if (
                              currentChoices &&
                              currentChoices.some((choice: string) => choice && choice.trim() !== "")
                            ) {
                              console.log("Triggering validation for response_choices");
                              trigger(`questions.${index}.response_choices`);
                            }
                          }
                        },
                      })}
                      placeholder={`Option ${optionIndex + 1}`}
                      className={`w-full rounded px-3 py-1 focus:bg-gray-100 outline-none ${isEmptyOption && questionError?.response_choices ? "border border-red-100" : ""}`}
                    />
                    {isEmptyOption && questionError?.response_choices && (
                      <p className="text-xs text-red-300 mt-0.5">Option text is required</p>
                    )}
                    {!isEmptyOption && duplicateOptionIndices?.has(optionIndex) && (
                      <p className="text-xs text-red-500 mt-0.5">Duplicate option</p>
                    )}
                  </div>
                  {!shouldHideRemoveButton && (
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:text-gray-700 flex-shrink-0"
                      onClick={() => handleRemoveOption(optionIndex)}
                    >
                      <XIcon className="w-4 h-4" strokeWidth={2} />
                    </button>
                  )}
                  {shouldHideRemoveButton && <div className="w-4 h-4" />}
                  {!hideSubQuestionMapping && (
                    <ZTooltip
                      content="Add sub-question based on this response"
                      placement="bottom"
                      showArrow
                      isDisabled={false}
                    >
                      <button
                        type="button"
                        className="text-xs text-gray-500 hover:text-blue-700 flex-shrink-0"
                        onClick={() => handleSubQuestionClick(optionIndex)}
                      >
                        <CornerDownRight className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </ZTooltip>
                  )}
                </div>
              </td>
              {!hideSubQuestionMapping && (
                <td className="w-1/2 pl-4">
                  {activeSubQuestionOption === optionIndex ? (
                    <div className="flex items-center gap-2">
                      <Select
                        placeholder="Select a question"
                        size="sm"
                        classNames={{
                          base: "w-full",
                          trigger: "min-h-8 h-8",
                        }}
                        selectedKeys={mappedSubQuestion ? [mappedSubQuestion.id] : []}
                        isOpen={true}
                        onOpenChange={(isOpen) => {
                          if (!isOpen) {
                            setActiveSubQuestionOption(null);
                          }
                        }}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleSubQuestionSelect(optionIndex, e.target.value);
                          }
                        }}
                      >
                        {getAvailableQuestions().map((q) => (
                          <SelectItem key={q.id} value={q.id} classNames={{ title: "truncate" }}>
                            {formatQuestionDisplay(q, false)}
                          </SelectItem>
                        ))}
                      </Select>
                      {mappedSubQuestion && (
                        <button
                          type="button"
                          className="text-xs text-gray-500 hover:text-red-600 flex-shrink-0"
                          onClick={() => handleRemoveSubQuestion(optionIndex)}
                        >
                          <XIcon className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ) : (
                    mappedSubQuestion && (
                      <div
                        className="flex items-center gap-2 text-sm text-gray-700 min-w-0 cursor-pointer"
                        onClick={() => handleSubQuestionClick(optionIndex)}
                      >
                        <TruncatedText text={formatQuestionDisplay(mappedSubQuestion, true)} className="flex-1" />
                        <button
                          type="button"
                          className="text-xs text-gray-500 hover:text-red-600 flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveSubQuestion(optionIndex);
                          }}
                        >
                          <XIcon className="w-3 h-3" />
                        </button>
                      </div>
                    )
                  )}
                </td>
              )}
            </tr>
          );
        })}
        <tr>
          <td>
            <ZButton
              variant="bordered"
              className="text-xs font-medium h-7 px-2 mt-2"
              onClick={handleAddOption}
              size="sm"
            >
              Add Option
            </ZButton>
          </td>
        </tr>
      </>
    );
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      if (itemRef) {
        (itemRef as any).current = node;
      }
    },
    [setNodeRef],
  );

  // Check if this question has any validation errors
  const hasError = isInvalid || !!questionError;

  return (
    <>
      <div
        ref={combinedRef}
        style={{
          ...style,
          transition: "all 0.3s ease",
        }}
        className={`mx-auto max-w-[80%] w-[75%] min-w-[420px] rounded-lg shadow-sm flex flex-col gap2 relative group hover:shadow-lg ${hasError ? "border-2 border-red-100" : "border border-[#E4E4E7]"
          }`}
      >
        <div className="bg-[#FAFAFA] px-4 py-3 flex text-zinc-700 h-[44px] rounded-lg">
          {/* Header */}
          {/* Only show drag handle for root questions (not sub-questions) */}
          {!watch(`questions.${index}.parent_question_id`) && (
            <span
              {...attributes}
              {...listeners}
              className="opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-gray-400 mr-1 border border-1 absolute p-0.5 rounded-md left-[-12px] top-[8px] bg-white"
            >
              <GripVertical size={18} />
            </span>
          )}
          <div className="grow text-sm font-medium flex items-center gap-2">{getQuestionDisplayNumber()}</div>
          <div className="flex  items-center gap-6">
            {!hideComments && (
              <div className="flex items-center gap-2">
                <Switch
                  size="sm"
                  isSelected={watch(`questions.${index}.comments_allowed`)}
                  onValueChange={(value) => {
                    setValue(`questions.${index}.comments_allowed`, value, { shouldDirty: true });
                  }}
                />
                <span className="text-xs text-gray-500">Allow Comments</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                size="sm"
                isSelected={!watch(`questions.${index}.required`)}
                onValueChange={(value) => {
                  setValue(`questions.${index}.required`, !value, { shouldDirty: true });
                }}
              />
              <span className="text-xs text-gray-500">Optional</span>
            </div>
            <div className="flex items-center gap-2">
              <Dropdown style={{ position: "absolute", top: "132px", right: "0" }}>
                <DropdownTrigger>
                  <Ellipsis className="h-5 w-5 text-gray-400 hover:text-gray-600 cursor-pointer" />
                </DropdownTrigger>
                <DropdownMenu
                  aria-label="Question Options"
                  onAction={(key) => {
                    if (key === "change-type-header") {
                      // Do nothing - this is just a header
                      return;
                    } else if (key === "duplicate") {
                      handleDuplicateQuestion();
                    } else if (key === "delete") {
                      handleDeleteClick();
                    } else if (QUESTION_TYPES.find((qt) => qt.value === key)) {
                      handleQuestionTypeChange(key as string);
                    }
                  }}
                >
                  <DropdownItem
                    key="change-type-header"
                    textValue="Change Question Type"
                    className="opacity-60 cursor-default"
                    isReadOnly
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-sm font-medium">Change Question Type</span>
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </DropdownItem>

                  <DropdownItem
                    key="free_text"
                    textValue="Text Field"
                    className={type === "free_text" ? "bg-blue-50" : ""}
                  >
                    <div className="flex items-center gap-2 pl-4">
                      <TextInitialIcon className="w-4 h-4" />
                      <span className="text-sm">Text Field</span>
                    </div>
                  </DropdownItem>

                  <DropdownItem
                    key="single_choice"
                    textValue="Single Choice"
                    className={type === "single_choice" ? "bg-blue-50" : ""}
                  >
                    <div className="flex items-center gap-2 pl-4">
                      <RadioIcon className="w-4 h-4" />
                      <span className="text-sm">Single Choice</span>
                    </div>
                  </DropdownItem>

                  <DropdownItem
                    key="multiple_choice"
                    textValue="Multiple Choice"
                    className={type === "multiple_choice" ? "bg-blue-50" : ""}
                  >
                    <div className="flex items-center gap-2 pl-4">
                      <CheckboxIcon className="w-4 h-4" />
                      <span className="text-sm">Multiple Choice</span>
                    </div>
                  </DropdownItem>

                  <DropdownItem key="dropdown" textValue="Dropdown" className={type === "dropdown" ? "bg-blue-50" : ""}>
                    <div className="flex items-center gap-2 pl-4">
                      <CircleChevronDownIcon className="w-4 h-4" />
                      <span className="text-sm">Dropdown</span>
                    </div>
                  </DropdownItem>

                  {showFileUploadType ? (
                    <DropdownItem
                      key="file_upload"
                      textValue="File Upload"
                      className={type === "file_upload" ? "bg-blue-50" : ""}
                    >
                      <div className="flex items-center gap-2 pl-4">
                        <FileUpIcon className="w-4 h-4" />
                        <span className="text-sm">File Upload</span>
                      </div>
                    </DropdownItem>
                  ) : null}

                  <DropdownItem key="duplicate" textValue="Duplicate">
                    <div className="flex items-center gap-2">
                      <Copy className="w-4 h-4" />
                      <span className="text-sm">Duplicate</span>
                    </div>
                  </DropdownItem>

                  <DropdownItem key="delete" textValue="Delete Question" className="text-red-300">
                    <div className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      <span className="text-sm">Delete Question</span>
                    </div>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
        </div>
        <div>
          {/* Question Details */}
          <div className="p-4">
            {/* Question */}
            <textarea
              {...register(`questions.${index}.question`, {
                onChange: () => {
                  // Check if there's an error and trigger validation
                  if (questionError?.question) {
                    const currentQuestion = getValues(`questions.${index}.question`);
                    if (currentQuestion && currentQuestion.trim() !== "") {
                      trigger(`questions.${index}.question`);
                    }
                  }
                },
              })}
              placeholder="Enter question text"
              className={`w-full flex-1 text-sm outline-none bg-transparent px-2 py-1 rounded focus:bg-gray-50 resize-none ${questionError?.question ? "border border-red-100" : "border-none"}`}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = "auto";
                target.style.height = `${target.scrollHeight}px`;
              }}
            />
            {questionError?.question && (
              <p className="text-xs text-red-300 mt-1 px-[9px]">{questionError.question.message}</p>
            )}

            <div className="flex text-sm flex-col">
              {/* Answer Section */}
              <table className="w-full text-gray-700 table-fixed">
                <thead>
                  <tr>
                    <th className="text-left font-medium w-1/2 pb-2">
                      {watch(`questions.${index}.is_dropdown`)
                        ? "Dropdown Options"
                        : optionsLabel[type as keyof typeof optionsLabel]}
                    </th>
                    <th className="text-left font-medium w-1/2 pb-2">
                      {hasAnySubQuestionsMapped() && "Sub-Question Based on Response"}
                    </th>
                  </tr>
                </thead>
                <tbody>{renderOptions()}</tbody>
              </table>
              {type !== "free_text" && questionError?.response_choices && (
                <p className="text-xs text-red-300 mt-2 px-2">{questionError.response_choices.message}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Question Modal */}
      <DeleteQuestionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteQuestion}
        loading={false}
      />
    </>
  );
};

export default QuestionItem;
